import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { supabase, db } from '@lib/supabase'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const AuthContext = createContext({})
// Exported for tests: the shared renderer injects an auth value through the context without running the provider.
export { AuthContext }

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [photographerProfile, setPhotographerProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const isMountedRef = useRef(true)
  const fetchInProgressRef = useRef(false)
  const currentFetchPromiseRef = useRef(null)

  // Fetch user profile and photographer data if applicable
  const fetchUserData = useCallback(async (user) => {
    // Prevent concurrent fetches - return the existing promise instead of null
    if (fetchInProgressRef.current && currentFetchPromiseRef.current) {
      console.log('[AuthContext] ⏭️  Fetch already in progress, returning existing promise')
      return currentFetchPromiseRef.current
    }

    fetchInProgressRef.current = true

    // Create a new promise and store it
    const fetchPromise = (async () => {
      try {
        return await executeFetch(user)
      } finally {
        fetchInProgressRef.current = false
        currentFetchPromiseRef.current = null
      }
    })()

    currentFetchPromiseRef.current = fetchPromise
    return fetchPromise
  }, [])

  // Separate the actual fetch logic
  const executeFetch = async (user) => {
    // Check if component is still mounted before proceeding
    if (!isMountedRef.current) {
      console.log('[AuthContext] Component unmounted, skipping fetch')
      fetchInProgressRef.current = false
      return null
    }
    try {
      console.log('[AuthContext] Fetching user data for:', user.id)

      // Get user profile with timeout and fallback
      console.log('[AuthContext] 🔍 Fetching user profile...')
      let userProfile = null

      try {
        // Try db.users.getProfile with 3s timeout (reduced for faster fallback)
        console.log('[AuthContext] 🔍 Starting Promise.race with 3s timeout...')
        const profilePromise = db.users.getProfile(user.id)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Profile fetch timeout')), 3000)
        )
        userProfile = await Promise.race([profilePromise, timeoutPromise])
        console.log('[AuthContext] ✅ Profile fetched successfully via db.users.getProfile')
      } catch (timeoutError) {
        console.warn('[AuthContext] ⏱️ Profile fetch timed out, will retry with direct query')

        // CRITICAL FIX: Instead of creating minimal profile, fetch directly from database
        // This prevents role mismatch errors when photographer profile exists but times out
        try {
          const { data: directProfile, error: directError } = await supabase
            .from('users')
            .select('*')
            .eq('id', user.id)
            .maybeSingle()

          if (directProfile && !directError) {
            userProfile = directProfile
            console.log('[AuthContext] ✅ Profile fetched successfully via direct query after timeout')
          } else {
            // Only create minimal profile if direct query also fails
            userProfile = {
              id: user.id,
              email: user.email,
              full_name: user.user_metadata?.full_name || '',
              role: user.user_metadata?.role || 'customer',
              created_at: user.created_at
            }
            console.log('[AuthContext] ✅ Created minimal profile from session metadata (direct query failed)')
          }
        } catch (directQueryError) {
          // Fallback to minimal profile if direct query fails
          userProfile = {
            id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name || '',
            role: user.user_metadata?.role || 'customer',
            created_at: user.created_at
          }
          console.log('[AuthContext] ✅ Created minimal profile from session metadata (direct query error)')
        }
      }

      console.log('[AuthContext] User profile:', userProfile)

      // If profile doesn't exist, create it
      if (!userProfile) {
        console.log('[AuthContext] Creating new user profile')

        try {
          // Check if user has an accepted talent application to determine role
          let userRole = user.user_metadata?.role || 'customer'

          try {
            console.log('[AuthContext] Checking for accepted talent application...')
            const { data: talentApp } = await supabase
              .from('talent_applications')
              .select('id, is_accepted')
              .eq('is_accepted', true)
              .or(`user_id.eq.${user.id},email.eq.${user.email}`)
              .maybeSingle()

            if (talentApp) {
              console.log('[AuthContext] ✅ Found accepted talent application, setting role to photographer')
              userRole = 'photographer'
            } else {
              console.log('[AuthContext] No accepted talent application found, using role:', userRole)
            }
          } catch (appError) {
            console.warn('[AuthContext] Failed to check talent applications:', appError.message)
          }

          // Add timeout to profile creation to prevent infinite hang
          const createPromise = supabase
            .from('users')
            .upsert({
              id: user.id,
              email: user.email,
              role: userRole,
              full_name: user.user_metadata?.full_name || '',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'id'
            })
            .select()
            .single()

          const createTimeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Profile creation timeout')), 5000)
          )

          const { data, error } = await Promise.race([createPromise, createTimeout])

          if (!error && data) {
            userProfile = data
            console.log('[AuthContext] ✅ Created user profile:', userProfile)
          } else {
            console.error('[AuthContext] ❌ Error creating profile:', error)
          }
        } catch (createError) {
          console.error('[AuthContext] ⏱️ Profile creation timed out or failed:', createError.message)
        }
      } else {
        // Profile exists - check if email needs to be synced from auth.users
        if (userProfile.email !== user.email) {
          console.log('[AuthContext] Email mismatch detected, syncing:', {
            profileEmail: userProfile.email,
            authEmail: user.email
          })

          const { data: updatedProfile, error: syncError } = await supabase
            .from('users')
            .update({
              email: user.email,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id)
            .select()
            .single()

          if (!syncError && updatedProfile) {
            userProfile = updatedProfile
            console.log('[AuthContext] ✅ Email synced successfully to users table')
          } else {
            console.error('[AuthContext] ❌ Failed to sync email:', syncError)
          }
        }
      }

      setProfile(userProfile)

      // If photographer, get photographer profile with timeout and retry
      if (userProfile?.role === 'photographer') {
        console.log('[AuthContext] Fetching photographer profile')
        try {
          const photographerPromise = db.photographers.getProfile(user.id)
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Photographer profile fetch timeout')), 3000)
          )
          const photographerData = await Promise.race([photographerPromise, timeoutPromise])
          console.log('[AuthContext] Photographer profile:', photographerData)
          setPhotographerProfile(photographerData)
        } catch (photographerError) {
          console.warn('[AuthContext] ⚠️ Photographer profile fetch timeout, retrying with direct query')

          // CRITICAL FIX: Retry with direct database query instead of giving up
          try {
            const { data: directPhotographerProfile, error: directPhotoError } = await supabase
              .from('photographers')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle()

            if (directPhotographerProfile && !directPhotoError) {
              console.log('[AuthContext] ✅ Photographer profile fetched via direct query')
              setPhotographerProfile(directPhotographerProfile)
            } else {
              console.warn('[AuthContext] ⚠️ Photographer profile not found, will be created if needed')
              setPhotographerProfile(null)
            }
          } catch (directPhotoQueryError) {
            console.warn('[AuthContext] ⚠️ Direct photographer profile query failed, will be created if needed')
            setPhotographerProfile(null)
          }
        }
      }

      console.log('[AuthContext] User data fetch complete')
      return userProfile
    } catch (error) {
      console.error('[AuthContext] Error fetching user data:', error)
      // Don't show error toast on initial load
      return null
    }
  } // Empty dependency array since it only uses parameters

  useEffect(() => {
  console.log("[AuthContext] 🚀 useEffect mounted");
  let isMounted = true

  const loadUser = async () => {
    try {
      setLoading(true)

      // Check if this is a password recovery flow - if so, skip normal auth handling
      // The ResetPassword page will handle the recovery session
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const isPasswordRecovery = hashParams.get('type') === 'recovery'

      if (isPasswordRecovery) {
        console.log('[AuthContext] Password recovery detected in URL, skipping normal auth flow')
        setLoading(false)
        return
      }

      console.log('[AuthContext] Checking session...')
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.user) {
        console.log('[AuthContext] No active session found')
        setUser(null)
        setProfile(null)
        setPhotographerProfile(null)
        setLoading(false)
        return
      }

      console.log('[AuthContext] Found session for user:', session.user.id)
      setUser(session.user)

      // 🔹 Call your Edge Function directly
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/get-user-data`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!res.ok) throw new Error(`Edge function failed: ${res.status}`)

      const data = await res.json()
      console.log('[AuthContext] ✅ Edge function returned:', data)

      if (!isMounted) return

      setProfile(data.profile || null)
      setPhotographerProfile(data.photographerProfile || null)
    } catch (err) {
      console.error('[AuthContext] ❌ Auth load error:', err)
    } finally {
      if (isMounted) setLoading(false)
    }
  }

  loadUser()

  // 🔔 Watch for sign-in / sign-out
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      console.log('[AuthContext] Auth event:', event)

      // Handle password recovery - just set session but don't do normal auth flow
      // This allows the ResetPassword page to handle the recovery
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[AuthContext] Password recovery event detected')
        if (session?.user) {
          setUser(session.user)
        }
        setLoading(false)
        // Don't fetch profile or redirect - let ResetPassword handle it
        return
      }

      if (event === 'SIGNED_OUT') {
        setUser(null)
        setProfile(null)
        setPhotographerProfile(null)
        setLoading(false)
      } else if (session?.user) {
        setUser(session.user)
        // Fetch profile data on sign-in events to ensure state is synchronized
        // This is important for flows like TalentApplication where signUp creates the user
        // and we need the profile/photographerProfile to be available immediately
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Fetching user data after auth event:', event)
          // Set loading to true while we fetch profile data
          // This ensures ProtectedRoute shows loading spinner until profile is ready
          setLoading(true)
          // Use a small delay to avoid race conditions with signUp's own fetchUserData call
          setTimeout(async () => {
            try {
              await fetchUserData(session.user)
            } finally {
              setLoading(false)
            }
          }, 100)
        }
      }
    }
  )

  return () => {
    isMounted = false
    subscription.unsubscribe()
  }
  }, [])
 // Empty dependency array - checkSession and fetchUserData are stable

  // Sign up function
  const signUp = async (email, password, userData = {}) => {
    try {
      const { role = 'customer', fullName, phone, isVideographer = false } = userData

      // First create the auth user without any metadata that might cause issues
      const { data, error } = await supabase.auth.signUp({
        email,
        password
      })

      if (error) throw error

      // Check if user was created successfully
      if (!data?.user) {
        throw new Error('Failed to create user account')
      }

      // If email confirmation is disabled, sign in automatically
      // If identities array exists and has length > 0, user is confirmed
      const isConfirmed = data.user.identities && data.user.identities.length > 0

      // Create or update profile after signup
      if (data?.user) {
        // First, ensure the user record exists in public.users
        const { error: upsertError } = await supabase
          .from('users')
          .upsert({
            id: data.user.id,
            email: data.user.email,
            full_name: fullName,
            phone,
            role,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'id'
          })

        if (upsertError) {
          console.error('Error creating user profile:', upsertError)
        }

        // If photographer or videographer, initialize photographer profile with required fields
        if (role === 'photographer') {
          console.log('[AuthContext] 📸 Creating photographer profile for:', data.user.id)

          // Create record in photographers table
          const { data: photographerData, error: photographerError} = await supabase
            .from('photographers')
            .upsert({
              user_id: data.user.id,
              is_public: true,
              profile_complete: false,
              is_videographer: isVideographer,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id'
            })
            .select()

          if (photographerError) {
            console.error('[AuthContext] ❌ Error creating photographer profile:', photographerError)
          } else {
            console.log('[AuthContext] ✅ Photographer profile created successfully:', photographerData)
          }

          // CRITICAL: Also create record in photographer_preview_profiles for search visibility
          const { data: previewData, error: previewError } = await supabase
            .from('photographer_preview_profiles')
            .upsert({
              user_id: data.user.id,
              display_name: fullName || 'New Photographer',
              contact_email: email,
              contact_phone: phone || null,
              bio: null,  // Will be filled during profile completion
              specialties: [],  // Will be filled during profile completion
              location_city: 'Unknown',  // Will be updated during profile completion
              location_state: 'Unknown',  // Will be updated during profile completion
              is_available: true,  // CRITICAL: Must be true to appear in search
              is_verified: false,
              portfolio_images: [],  // Will be filled during profile completion
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id'
            })
            .select()

          if (previewError) {
            console.error('[AuthContext] ❌ Error creating preview profile:', previewError)
          } else {
            console.log('[AuthContext] ✅ Preview profile created successfully:', previewData)
          }
        }
      }

      // If user is confirmed, sign them in automatically
      if (isConfirmed) {
        // Auto sign-in after successful signup
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        })
        
        if (signInError) {
          console.warn('Auto sign-in failed:', signInError)
          toast.success('Account created! Please sign in.')
        } else {
          toast.success('Account created and signed in successfully!')
          // Set user state
          if (signInData?.user) {
            setUser(signInData.user)
            await fetchUserData(signInData.user)
          }
        }
      } else {
        toast.success('Please check your email to confirm your account')
      }
      
      return { success: true, user: data.user, isConfirmed }
    } catch (error) {
      console.error('Signup error:', error)
      toast.error(error.message || 'Failed to create account')
      return { success: false, error: error.message }
    }
  }

  // Sign in function
  // Options: { skipRedirect: boolean } - skip automatic navigation after sign in
  const signIn = async (email, password, options = {}) => {
    const { skipRedirect = false } = options
    try {
      console.log('[AuthContext] Starting sign in for:', email)

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        console.error('[AuthContext] Auth error:', error)
        throw error
      }

      if (!data.user) {
        throw new Error('No user data returned from authentication')
      }

      console.log('[AuthContext] Auth successful, user ID:', data.user.id)

      // Set user immediately
      setUser(data.user)

      // Fetch and set user profile data before navigation
      try {
        console.log('[AuthContext] Fetching user profile...')

        // Add timeout to profile fetch (5 seconds max)
        const fetchWithTimeout = Promise.race([
          fetchUserData(data.user),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
          )
        ])

        const userProfile = await fetchWithTimeout

        console.log('[AuthContext] Profile fetched:', userProfile)

        if (!userProfile) {
          console.error('[AuthContext] No profile returned')
          throw new Error('Failed to load user profile')
        }

        // Check if account is blacklisted or soft-deleted
        if (userProfile.is_blacklisted || userProfile.soft_deleted) {
          console.warn('[AuthContext] Account is blacklisted/deleted, logging out')
          await supabase.auth.signOut()
          setUser(null)
          setProfile(null)
          setPhotographerProfile(null)
          toast.error('This account has been suspended. Please contact support.')
          return { success: false, error: 'Account suspended' }
        }

        // Set profile state
        setProfile(userProfile)

        // If photographer, fetch photographer profile
        if (userProfile.role === 'photographer') {
          console.log('[AuthContext] Fetching photographer profile...')

          const photoFetchWithTimeout = Promise.race([
            db.photographers.getProfile(data.user.id),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Photographer profile timeout')), 3000)
            )
          ])

          try {
            const photographerData = await photoFetchWithTimeout
            setPhotographerProfile(photographerData)
            console.log('[AuthContext] Photographer profile loaded')
          } catch (photoError) {
            console.warn('[AuthContext] Photographer profile fetch failed:', photoError)
            // Continue anyway - photographer profile is optional for initial login
          }
        }

        // Show success message
        toast.success('Welcome back!')

        // Skip navigation if requested (caller will handle navigation)
        if (skipRedirect) {
          console.log('[AuthContext] Skipping redirect as requested')
          return { success: true, user: data.user, profile: userProfile }
        }

        // Navigate based on role immediately (no setTimeout)
        console.log('[AuthContext] Navigating to dashboard for role:', userProfile.role)

        if (userProfile.role === 'photographer') {
          // Check if videographer and training status by fetching photographer profile
          try {
            const photographerData = await db.photographers.getProfile(data.user.id)

            // Check if training is completed
            if (photographerData && !photographerData.training_completed) {
              console.log('[AuthContext] Training not completed, routing to training page')
              navigate('/talent/training', { replace: true })
              return { success: true, user: data.user, profile: userProfile }
            }

            // Training completed, route to appropriate dashboard
            if (photographerData?.is_videographer) {
              console.log('[AuthContext] Routing videographer to videographer dashboard')
              navigate('/talent/dashboard/videographer', { replace: true })
            } else {
              console.log('[AuthContext] Routing photographer to photographer dashboard')
              navigate('/talent/dashboard', { replace: true })
            }
          } catch (error) {
            console.warn('[AuthContext] Could not determine videographer status, using default route')
            navigate('/talent/dashboard', { replace: true })
          }
        } else if (userProfile.role === 'admin') {
          navigate('/lp-console', { replace: true })
        } else {
          navigate('/dashboard', { replace: true })
        }

        return { success: true, user: data.user, profile: userProfile }
      } catch (profileError) {
        console.error('[AuthContext] Profile fetch error:', profileError)

        // Show error but still try to navigate
        toast.error('Welcome back! Loading profile...')

        // Skip fallback navigation if requested
        if (!skipRedirect) {
          // Fallback navigation after short delay to allow error message to show
          setTimeout(() => {
            navigate('/dashboard', { replace: true })
          }, 500)
        }

        return { success: true, user: data.user, profileError: profileError.message }
      }
    } catch (error) {
      console.error('[AuthContext] Sign in error:', error)

      // Provide user-friendly error messages
      let errorMessage = 'Invalid credentials'

      if (error.message?.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password'
      } else if (error.message?.includes('Email not confirmed')) {
        errorMessage = 'Please confirm your email address before signing in'
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'Connection timeout. Please try again.'
      } else if (error.message) {
        errorMessage = error.message
      }

      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Sign out function
  const signOut = async () => {
    try {
      // Clear local state first
      setUser(null)
      setProfile(null)
      setPhotographerProfile(null)
      
      // Clear localStorage session data
      localStorage.removeItem('lovep-auth')
      localStorage.removeItem('sb-ldxscjxoakqrmkgqwwhr-auth-token')
      
      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut()
      
      // Even if there's an error, we've cleared local state
      if (error) {
        console.warn('Supabase signout warning:', error)
        // Don't throw - we still want to clear local state and redirect
      }
      
      toast.success('Signed out successfully')
      
      // Use setTimeout to ensure state updates are processed before navigation
      setTimeout(() => {
        navigate('/')
      }, 100)
      
      return { success: true }
    } catch (error) {
      console.error('Signout error:', error)
      // Even on error, clear local state
      setUser(null)
      setProfile(null)
      setPhotographerProfile(null)
      
      // Clear localStorage session data
      localStorage.removeItem('lovep-auth')
      localStorage.removeItem('sb-ldxscjxoakqrmkgqwwhr-auth-token')
      
      // Still navigate away
      setTimeout(() => {
        navigate('/')
      }, 100)
      
      toast.error('Sign out completed with errors')
      return { success: false, error: error.message }
    }
  }

  // Sign in with Google (OAuth)
  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      if (error) throw error
      // Browser will redirect to Google — no further action needed here
      return { success: true }
    } catch (error) {
      console.error('[AuthContext] Google sign-in error:', error)
      toast.error(error.message || 'Failed to sign in with Google')
      return { success: false, error: error.message }
    }
  }

  // Reset password
  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) throw error

      toast.success('Password reset email sent! Check your inbox.')
      return { success: true }
    } catch (error) {
      console.error('Password reset error:', error)
      toast.error(error.message || 'Failed to send reset email')
      return { success: false, error: error.message }
    }
  }

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      toast.success('Password updated successfully')
      return { success: true }
    } catch (error) {
      console.error('Update password error:', error)
      toast.error(error.message || 'Failed to update password')
      return { success: false, error: error.message }
    }
  }

  // Update profile
  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('Not authenticated')

      const updatedProfile = await db.users.updateProfile(user.id, updates)
      setProfile(updatedProfile)

      // If photographer-specific updates
      if (profile?.role === 'photographer' && updates.photographer) {
        const photographerUpdates = await db.photographers.updateProfile(
          photographerProfile.id,
          updates.photographer
        )
        setPhotographerProfile(photographerUpdates)
      }

      toast.success('Profile updated successfully')
      return { success: true, profile: updatedProfile }
    } catch (error) {
      console.error('Profile update error:', error)
      toast.error('Failed to update profile')
      return { success: false, error: error.message }
    }
  }

  // Check if user has completed onboarding
  const checkOnboardingStatus = () => {
    if (!profile) return null
    
    if (profile.role === 'photographer') {
      return photographerProfile?.onboarding_completed || false
    }
    
    // Customers don't need onboarding
    return true
  }

  // Helper to check user role
  const hasRole = (role) => {
    return profile?.role === role
  }

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user && !!profile
  }

  // Refresh profile data (useful after profile updates)
  const refreshProfile = useCallback(async () => {
    if (!user) return
    console.log('[AuthContext] Refreshing profile data')
    await fetchUserData(user)
  }, [user, fetchUserData])

  const value = {
    user,
    profile,
    photographerProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    fetchUserData,
    refreshProfile,
    checkOnboardingStatus,
    hasRole,
    isAuthenticated
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Protected route wrapper
export const ProtectedRoute = ({ children, requireRole }) => {
  const { user, profile, loading } = useAuth()
  const location = useLocation()

  if (loading || (requireRole && user && !profile))
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-t-2 border-b-2 border-purple-600 rounded-full" />
      </div>
    )

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  if (requireRole && profile?.role !== requireRole && profile?.role !== 'admin') {
    return <Navigate to="/forbidden" replace />
  }

  return children
}
